import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Footer from "@/components/Footer";

const HERO = process.env.PUBLIC_URL + "/nextrota-hero.png";
const LOGO_DARK = process.env.PUBLIC_URL + "/nextrota-logo.png";
const LOGO_ICON = process.env.PUBLIC_URL + "/nextrota-logo-icon.png";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/app");
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Hero panel - the artwork is the brand statement; we keep the
          overlay minimal so the official NextRota lockup baked into the
          image remains the focal point. */}
      <div className="relative hidden lg:flex flex-col justify-end p-12 bg-[#0B1A36] overflow-hidden">
        <img src={HERO} alt=""
          className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#040B1E]/90 via-[#040B1E]/40 to-transparent"/>

        <div className="relative z-10 text-white max-w-md">
          <div className="eyebrow text-white/70 mb-3">Workforce planning</div>
          <p className="text-white text-2xl font-medium leading-snug mb-3">Your workforce command centre</p>
          <p className="text-white/70 text-sm max-w-sm">Plan rotas, manage leave and keep staffing decisions clear in one calm workspace.</p>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-3 mb-10" data-testid="login-brand">
            <img src={LOGO_DARK} alt="NextRota" className="h-10 w-auto"/>
          </Link>

          <div className="eyebrow mb-2">Sign in</div>
          <h2 className="font-display text-3xl mb-2">Welcome back</h2>
          <p className="text-[#4B5773] text-sm mb-8">Sign in to manage your rota, team and workforce planning.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-[#4B5773]">Work email</Label>
              <Input id="email" type="email" value={email} required
                data-testid="login-email-input"
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 mt-2 rounded-lg border-slate-200 focus-visible:ring-[#2563EB]/30 focus-visible:border-[#2563EB]" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="pw" className="text-xs uppercase tracking-widest text-[#4B5773]">Password</Label>
                <Link to="/forgot-password" data-testid="forgot-password-link"
                  className="text-xs text-[#2563EB] hover:underline">
                  Forgot password?
                </Link>
              </div>
              <Input id="pw" type="password" value={password} required
                data-testid="login-password-input"
                onChange={(e) => setPassword(e.target.value)}
                className="h-11 mt-2 rounded-lg border-slate-200 focus-visible:ring-[#2563EB]/30 focus-visible:border-[#2563EB]" />
            </div>
            <Button type="submit" disabled={busy} data-testid="login-submit-btn"
              className="w-full h-11 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-medium">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="text-sm text-[#4B5773] mt-6 text-center">
            No account?{" "}
            <Link to="/start" data-testid="goto-register-link" className="text-[#2563EB] font-medium hover:underline">
              Create a company workspace
            </Link>
          </p>

          <Footer variant="inline"/>
        </div>
      </div>
    </div>
  );
}

// Compact brand mark for any narrow surface that wants only the icon.
export function NextRotaMark({ size = 32, className = "" }) {
  return <img src={LOGO_ICON} alt="NextRota" width={size} height={size} className={className}/>;
}
